import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useForm } from "@formspree/react";
import api from "../../lib/api";
import { submitToFormspree } from "../../lib/formspree";
import { ArrowUpRight } from "@phosphor-icons/react";

const VESSEL_TYPES = [
    "Bulk Carrier", "Product Tanker", "Crude Tanker", "Chemical Tanker",
    "Container Vessel", "Offshore Support Vessel", "Project Cargo Vessel", "Other",
];

const initial = {
    name: "", email: "", company: "", phone: "",
    vessel_type: VESSEL_TYPES[0], cargo_type: "", origin: "",
    destination: "", laycan: "", details: "",
};

export default function CharterForm() {
    const [data, setData] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [, , formspreeReset] = useForm("mwvzvlqa");

    const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                _subject: `Windermere · Charter Request · ${data.vessel_type} · ${data.origin || "TBA"} → ${data.destination || "TBA"}`,
                _form: "charter",
                ...data,
            };
            const [fsRes, dbRes] = await Promise.allSettled([
                submitToFormspree(payload),
                api.post("/charter-request", data),
            ]);
            const ok = (fsRes.status === "fulfilled" && fsRes.value) || dbRes.status === "fulfilled";
            if (ok) {
                toast.success(dbRes?.value?.data?.message || "Charter inquiry received. Our chartering desk will revert shortly.");
                setData(initial);
                formspreeReset?.();
            } else {
                toast.error("Could not submit charter request");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.form
            onSubmit={submit}
            data-testid="charter-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="wms-glass p-7 md:p-10"
        >
            <div className="flex items-center justify-between mb-8">
                <div>
                    <span className="wms-overline">Charter Desk</span>
                    <h3 className="mt-3 font-display text-2xl md:text-3xl">Request a Vessel Charter</h3>
                </div>
                <span className="hidden md:flex text-[10px] tracking-[0.32em] uppercase text-white/40">Reply &lt; 24h</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
                {[
                    ["name", "Full Name *", "text", true],
                    ["email", "Work Email *", "email", true],
                    ["company", "Company", "text", false],
                    ["phone", "Phone", "tel", false],
                    ["origin", "Load Port", "text", false],
                    ["destination", "Discharge Port", "text", false],
                    ["cargo_type", "Cargo Type", "text", false],
                    ["laycan", "Laycan Window", "text", false],
                ].map(([k, l, t, req]) => (
                    <label key={k} className="bg-[#040914] p-4">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{l}</div>
                        <input
                            type={t}
                            required={req}
                            name={k}
                            value={data[k]}
                            onChange={update(k)}
                            data-testid={`charter-input-${k}`}
                            className="mt-2 w-full bg-transparent border-0 text-white outline-none placeholder-white/20"
                        />
                    </label>
                ))}
                <label className="md:col-span-2 bg-[#040914] p-4">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Vessel Type *</div>
                    <select
                        required
                        name="vessel_type"
                        value={data.vessel_type}
                        onChange={update("vessel_type")}
                        data-testid="charter-vessel-type"
                        className="mt-2 w-full bg-transparent border-0 text-white outline-none"
                    >
                        {VESSEL_TYPES.map((v) => (
                            <option key={v} value={v} className="bg-[#0B1324] text-white">{v}</option>
                        ))}
                    </select>
                </label>
                <label className="md:col-span-2 bg-[#040914] p-4">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Operational Notes</div>
                    <textarea
                        rows={4}
                        name="details"
                        value={data.details}
                        onChange={update("details")}
                        data-testid="charter-details"
                        className="mt-2 w-full bg-transparent border-0 text-white outline-none placeholder-white/20 resize-none"
                        placeholder="Quantity, parcel size, additional fixture remarks..."
                    />
                </label>
            </div>

            <button
                type="submit"
                disabled={loading}
                data-testid="charter-submit-btn"
                className="mt-8 group inline-flex items-center gap-3 px-7 py-4 bg-white text-[#040914] text-xs uppercase tracking-[0.24em] font-medium hover:bg-[#48CAE4] transition-colors disabled:opacity-60"
            >
                {loading ? "Submitting..." : "Send to Charter Desk"}
                <ArrowUpRight size={16} weight="bold" />
            </button>
        </motion.form>
    );
}
